import React, { useState, useRef } from 'react';
import { Building2, Mail, Phone, MapPin, Clock, AlertCircle, Beer, Music, Tv, Users, DollarSign, FileText, Upload, Image, Camera, Menu, Utensils, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { useLanguage } from '../contexts/LanguageContext';
import LocationSearch from '../components/LocationSearch';
import { uploadVenuePicture, updateVenuePictures, validateImageFile } from '../lib/storage';

type VenueType = 'restaurant' | 'bar';
type PictureCategory = 'gallery' | 'menu' | 'interior' | 'exterior';

interface PictureUpload {
  file: File;
  preview: string;
  category: PictureCategory;
}

const RestaurantSignup = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [venueType, setVenueType] = useState<VenueType>('restaurant');
  const [selectedCategory, setSelectedCategory] = useState<PictureCategory>('gallery');
  const [pictures, setPictures] = useState<PictureUpload[]>([]);
  const [selectedDietaryOptions, setSelectedDietaryOptions] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadedVenueId, setUploadedVenueId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    location_lat: null as number | null,
    location_lng: null as number | null,
    description: '',
    price_range: '',
    dietary_options: [] as string[],
    
    // Restaurant specific fields
    opening_hours: '',
    cuisine_type: '',
    
    // Bar specific fields
    opening_hours_bar: '',
    closing_hours: '',
    bar_type: '',
    features: {
      sports_tv: false,
      live_music: false,
      outdoor_seating: false,
      pool_tables: false,
      dart_boards: false,
      karaoke: false,
      happy_hour: false,
      food_service: false,
      smoking_allowed: false,
      wheelchair_accessible: false,
      parking_available: false,
      reservations_accepted: false
    },
    drinks_menu: {
      beer: {
        draft: [] as string[],
        bottled: [] as string[],
        craft: [] as string[]
      },
      wine: {
        red: [] as string[],
        white: [] as string[],
        sparkling: [] as string[]
      },
      cocktails: [] as string[],
      spirits: [] as string[]
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      address: location.address,
      location_lat: location.lat,
      location_lng: location.lng
    }));
  };

  const handleDietaryOptionToggle = (option: string) => {
    setFormData(prev => {
      const newOptions = prev.dietary_options.includes(option)
        ? prev.dietary_options.filter(o => o !== option)
        : [...prev.dietary_options, option];
      return {
        ...prev,
        dietary_options: newOptions
      };
    });
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature as keyof typeof prev.features]
      }
    }));
  };

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return;
    
    const newPictures: PictureUpload[] = [];
    const files = Array.from(e.target.files);
    let hasErrors = false;

    for (const file of files) {
      // Validate the image file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid image file');
        hasErrors = true;
        continue;
      }

      const preview = URL.createObjectURL(file);
      newPictures.push({
        file,
        preview,
        category: selectedCategory
      });
    }

    if (!hasErrors) {
      setError(null);
    }

    setPictures(prev => [...prev, ...newPictures]);
    
    // Log for debugging
    console.log(`Added ${newPictures.length} pictures in category ${selectedCategory}`);
    console.log('Current pictures state:', [...pictures, ...newPictures]);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePicture = (index: number) => {
    setPictures(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPictures = async (venueId: string): Promise<{ [key in PictureCategory]: string[] }> => {
    const uploadedUrls: { [key in PictureCategory]: string[] } = {
      gallery: [],
      menu: [],
      interior: [],
      exterior: []
    };

    console.log(`Starting upload of ${pictures.length} pictures for venue ${venueId}`);
    setUploadStatus('Uploading pictures...');
    
    // Calculate total for progress tracking
    const totalPictures = pictures.length;
    let completedPictures = 0;
    let failedPictures = 0;

    for (const picture of pictures) {
      try {
        setUploadStatus(`Uploading ${picture.file.name} (${completedPictures + 1}/${totalPictures})`);
        console.log(`Uploading ${picture.file.name} in category ${picture.category}`);
        
        const publicUrl = await uploadVenuePicture(venueId, picture.file, picture.category);
        
        console.log(`Upload successful, URL: ${publicUrl}`);
        uploadedUrls[picture.category].push(publicUrl);
        
        // Update progress
        completedPictures++;
        setUploadProgress(Math.round((completedPictures / totalPictures) * 100));
      } catch (err) {
        console.error(`Error uploading picture ${picture.file.name}:`, err);
        failedPictures++;
        // Continue with other pictures instead of failing completely
      }
    }

    console.log('All uploads completed. Result:', uploadedUrls);
    setUploadStatus(`Uploads completed. ${completedPictures} successful, ${failedPictures} failed.`);
    return uploadedUrls;
  };

  // Check if a venue with the same name and email already exists
  const checkVenueExists = async (): Promise<boolean> => {
    try {
      const table = venueType === 'restaurant' ? 'restaurants' : 'bars';
      const { data, error } = await supabase
        .from(table)
        .select('id')
        .eq('name', formData.name)
        .eq('email', formData.email)
        .maybeSingle();

      if (error) {
        console.error(`Error checking if ${venueType} exists:`, error);
        throw error;
      }

      return !!data;
    } catch (err) {
      console.error(`Error checking if ${venueType} exists:`, err);
      return false; // Return false to allow the process to continue
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('');
    setUploadedVenueId(null);

    try {
      // Check if venue with same name and email already exists
      const venueExists = await checkVenueExists();
      if (venueExists) {
        throw new Error(`A ${venueType} with this name and email already exists. Please use a different name or email.`);
      }

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Please sign in to register a venue');

      console.log('Starting venue registration process');
      console.log('Form data:', formData);
      console.log('Pictures to upload:', pictures);
      setUploadStatus('Creating venue...');

      let venueId: string;

      if (venueType === 'restaurant') {
        // Create the restaurant with empty pictures object
        const { data: restaurant, error: restaurantError } = await supabase
          .from('restaurants')
          .insert({
            owner_id: user.id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            location_lat: formData.location_lat,
            location_lng: formData.location_lng,
            opening_hours: formData.opening_hours,
            cuisine_type: formData.cuisine_type,
            description: formData.description,
            price_range: formData.price_range,
            dietary_options: formData.dietary_options,
            pictures: {
              gallery: [],
              menu: [],
              interior: [],
              exterior: []
            }
          })
          .select()
          .single();

        if (restaurantError) {
          console.error('Error creating restaurant:', restaurantError);
          throw restaurantError;
        }
        
        console.log('Restaurant created successfully:', restaurant);
        venueId = restaurant.id;
      } else {
        // Create the bar with empty pictures object
        const { data: bar, error: barError } = await supabase
          .from('bars')
          .insert({
            owner_id: user.id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            location_lat: formData.location_lat,
            location_lng: formData.location_lng,
            opening_hours: formData.opening_hours_bar,
            closing_hours: formData.closing_hours,
            description: formData.description,
            price_range: formData.price_range,
            bar_type: formData.bar_type,
            features: formData.features,
            drinks_menu: formData.drinks_menu,
            pictures: {
              gallery: [],
              menu: [],
              interior: [],
              exterior: []
            }
          })
          .select()
          .single();

        if (barError) {
          console.error('Error creating bar:', barError);
          throw barError;
        }
        
        console.log('Bar created successfully:', bar);
        venueId = bar.id;
      }
      
      // Save the venue ID for potential retry operations
      setUploadedVenueId(venueId);

      // Upload pictures if any
      if (pictures.length > 0) {
        console.log(`Uploading ${pictures.length} pictures for venue ${venueId}`);
        const uploadedUrls = await uploadPictures(venueId);
        console.log('Pictures uploaded successfully, updating venue with URLs:', uploadedUrls);
        
        // Check if any pictures were actually uploaded
        const totalUploadedPictures = Object.values(uploadedUrls).flat().length;
        if (totalUploadedPictures === 0) {
          console.warn('No pictures were successfully uploaded');
          setUploadStatus('Warning: No pictures were successfully uploaded');
        } else {
          setUploadStatus('Updating venue with picture URLs...');
          
          try {
            // Update the venue with the picture URLs
            await updateVenuePictures(venueId, venueType, uploadedUrls);
            console.log('Venue pictures updated successfully');
            
            // Verify the pictures were saved correctly
            const { data: verifyVenue, error: verifyError } = await supabase
              .from(venueType === 'restaurant' ? 'restaurants' : 'bars')
              .select('pictures')
              .eq('id', venueId)
              .single();
              
            if (verifyError) {
              console.error('Error verifying venue pictures:', verifyError);
            } else {
              console.log('Verified venue pictures:', verifyVenue.pictures);
              
              // Check if all uploaded URLs are in the database
              let allUrlsSaved = true;
              let missingUrls = [];
              
              for (const category in uploadedUrls) {
                const typedCategory = category as PictureCategory;
                for (const url of uploadedUrls[typedCategory]) {
                  if (!verifyVenue.pictures[typedCategory]?.includes(url)) {
                    allUrlsSaved = false;
                    missingUrls.push(url);
                    console.error(`URL not found in database: ${url}`);
                  }
                }
              }
              
              if (!allUrlsSaved) {
                console.warn('Some uploaded URLs were not saved in the database:', missingUrls);
                
                // Try one more time to update the pictures
                try {
                  console.log('Attempting to fix missing URLs with a second update...');
                  
                  // Get the latest venue data
                  const { data: latestVenue, error: latestError } = await supabase
                    .from(venueType === 'restaurant' ? 'restaurants' : 'bars')
                    .select('pictures')
                    .eq('id', venueId)
                    .single();
                  
                  if (latestError) {
                    console.error('Error getting latest venue data:', latestError);
                  } else {
                    // Create a new pictures object with all the URLs
                    const fixedPictures = {
                      gallery: [
                        ...(Array.isArray(latestVenue.pictures.gallery) ? latestVenue.pictures.gallery : []),
                        ...uploadedUrls.gallery
                      ],
                      menu: [
                        ...(Array.isArray(latestVenue.pictures.menu) ? latestVenue.pictures.menu : []),
                        ...uploadedUrls.menu
                      ],
                      interior: [
                        ...(Array.isArray(latestVenue.pictures.interior) ? latestVenue.pictures.interior : []),
                        ...uploadedUrls.interior
                      ],
                      exterior: [
                        ...(Array.isArray(latestVenue.pictures.exterior) ? latestVenue.pictures.exterior : []),
                        ...uploadedUrls.exterior
                      ]
                    };
                    
                    // Update the venue with the fixed pictures
                    const { error: fixError } = await supabase
                      .from(venueType === 'restaurant' ? 'restaurants' : 'bars')
                      .update({ pictures: fixedPictures })
                      .eq('id', venueId);
                    
                    if (fixError) {
                      console.error('Error fixing missing URLs:', fixError);
                      setUploadStatus('Warning: Some pictures may not have been saved correctly');
                    } else {
                      console.log('Fixed missing URLs successfully');
                      setUploadStatus('All pictures uploaded and saved successfully!');
                    }
                  }
                } catch (fixErr) {
                  console.error('Error during fix attempt:', fixErr);
                  setUploadStatus('Warning: Some pictures may not have been saved correctly');
                }
              } else {
                console.log('All uploaded URLs were saved in the database');
                setUploadStatus('All pictures uploaded and saved successfully!');
              }
            }
          } catch (updateError) {
            console.error('Error updating venue pictures:', updateError);
            setUploadStatus('Venue created but there was an error saving pictures. You can add them later.');
          }
        }
      } else {
        console.log('No pictures to upload');
        setUploadStatus('Venue created successfully (no pictures to upload)');
      }

      // Wait a moment to ensure all operations are complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Redirect to My Venues page
      navigate('/my-venues');
    } catch (err) {
      console.error('Error during venue registration:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during registration');
      
      // If we have a venue ID but encountered an error during picture upload,
      // we can still allow the user to continue to their venue page
      if (uploadedVenueId) {
        setUploadStatus('Venue was created but there were issues with picture uploads. You can add pictures later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-2xl font-bold text-gray-900">{t('venue.register.title')}</h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Venue Type Selection */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <h2 className="text-2xl font-bold mb-6">{t('venue.register.title')}</h2>
        
        <div className="flex space-x-4 mb-8">
          <button
            type="button"
            onClick={() => setVenueType('Restaurant')}
            className={`flex-1 py-3 px-6 rounded-lg font-medium transition ${
              venueType === 'Restaurant'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>{t('venue.register.restaurant')}</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setVenueType('Bar')}
            className={`flex-1 py-3 px-6 rounded-lg font-medium transition ${
              venueType === 'Bar'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Beer className="h-5 w-5" />
              <span>{t('venue.register.bar')}</span>
            </div>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Picture Upload Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">{t('venue.register.pictures')}</h3>
            
            {/* Category Selection */}
            <div className="flex flex-wrap space-x-2 mb-4">
              <button
                type="button"
                onClick={() => setSelectedCategory('gallery')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 mb-2 ${
                  selectedCategory === 'gallery'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Image className="h-4 w-4" />
                <span>Galerie</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCategory('menu')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 mb-2 ${
                  selectedCategory === 'menu'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Menu className="h-4 w-4" />
                <span>Menü</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCategory('interior')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 mb-2 ${
                  selectedCategory === 'interior'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Camera className="h-4 w-4" />
                <span>Innenbereiche</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedCategory('exterior')}
                className={`px-4 py-2 rounded-md flex items-center space-x-2 mb-2 ${
                  selectedCategory === 'exterior'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Building2 className="h-4 w-4" />
                <span>Aussenbereiche</span>
              </button>
            </div>

            {/* Upload Button */}
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {loading ? (
                    <>
                      <Loader className="h-6 w-6 animate-spin text-orange-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        Lädt... {uploadProgress}%
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Klicke zum hochladen</span> oder drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG oder JPEG (MAX. 5MB pro Bild)
                      </p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handlePictureUpload}
                  disabled={loading}
                />
              </label>
            </div>

            {/* Picture Previews */}
            {pictures.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Pictures ({pictures.length})</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {pictures.map((picture, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={picture.preview}
                        alt={`Upload preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => removePicture(index)}
                          className="text-white hover:text-red-500 transition-colors"
                          disabled={loading}
                        >
                          Entfernen
                        </button>
                      </div>
                      <span className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded">
                        {picture.category}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Common Fields */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4" />
                  <span>{venueType === 'restaurant' ? t('venue.register.restaurant') : t('venue.register.bar')} {t('common.name')}</span>
                </div>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder={`Füge ${venueType} Name ein`}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>{t('Email Adresse')}</span>
                </div>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder="Email Adresse"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>{t('Telefon Nummer')}</span>
                </div>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder="Telefon Nummer eingeben"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>{t('common.location')}</span>
                </div>
              </label>
              <LocationSearch
                onLocationSelect={handleLocationSelect}
                defaultValue={formData.address}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4" />
                  <span>{t('search.priceRange')}</span>
                </div>
              </label>
              <select
                name="price_range"
                value={formData.price_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
                disabled={loading}
              >
                <option value="">Preisklasse wählen</option>
                <option value="€">€ Preiswert (bis zu 30€)</option>
                <option value="€€">€€ Moderat (30-60€)</option>
                <option value="€€€">€€€ Gehoben (60-100€)</option>
                <option value="€€€€">€€€€ Luxuriös (over 100€)</option>
              </select>
            </div>
          </div>

          {/* Restaurant-specific Fields */}
          {venueType === 'restaurant' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{t('Öffnungszeiten')}</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="opening_hours"
                    value={formData.opening_hours}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="zB. Mo-So: 11:00 - 22:00"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('search.cuisine')}</label>
                  <select
                    name="cuisine_type"
                    value={formData.cuisine_type}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                    disabled={loading}
                  >
                    <option value="">Wähle deine Küche</option>
                    <option value="italian">Italienisch</option>
                    <option value="japanese">Japanisch</option>
                    <option value="mexican">Mexikanisch</option>
                    <option value="indian">Indisch</option>
                    <option value="american">Amerikanisch</option>
                    <option value="chinese">Chinesisch</option>
                    <option value="french">Französisch</option>
                    <option value="mediterranean">Mediterran</option>
                    <option value="thai">Thai</option>
                    <option value="vietnamese">Vietnamesisch</option>
                  </select>
                </div>
              </div>

              {/* Dietary Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Utensils className="h-4 w-4" />
                    <span>{t('common.dietaryOptions')}</span>
                  </div>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    'Vegetarisch',
                    'Vegan',
                    'Gluten-Frei',
                    'Halal',
                    'Kosher',
                    'Laktose-Frei',
                    'Regional',
                    'Saisonal',
                    'Umwelt Freundlich'
                  ].map((option) => (
                    <label key={option} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.dietary_options.includes(option)}
                        onChange={() => handleDietaryOptionToggle(option)}
                        className="rounded text-orange-500 focus:ring-orange-500"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-600">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Bar-specific Fields */}
          {venueType === 'bar' && (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{t('Öffnet um')}</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="opening_hours_bar"
                    value={formData.opening_hours_bar}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="zB. 16:00"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{t('Schließt um')}</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="closing_hours"
                    value={formData.closing_hours}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    placeholder="zB. 03:00"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.style')}</label>
                  <select
                    name="bar_type"
                    value={formData.bar_type}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                    disabled={loading}
                  >
                    <option value="">{t('Wähle Bartyp')}</option>
                    <option value="pub">Pub</option>
                    <option value="sports_bar">Sport Bar</option>
                    <option value="cocktail_lounge">Cocktail Lounge</option>
                    <option value="wine_bar">Wein Bar</option>
                    <option value="brewery">Brauerei</option>
                    <option value="dive_bar">Kneipe</option>
                    <option value="karaoke_bar">Karaoke Bar</option>
                    <option value="nightclub">Nachtclub</option>
                  </select>
                </div>
              </div>

              {/* Bar Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('venue.register.features')}</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(formData.features).map(([feature, enabled]) => (
                    <label key={feature} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => handleFeatureToggle(feature)}
                        className="rounded text-orange-500 focus:ring-orange-500"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-600">
                        {feature.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Common Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>{t('Beschreibung')}</span>
              </div>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md"
              rows={4}
              placeholder={`Erzähle uns über dein/e ${venueType}...`}
              required
              disabled={loading}
            />
          </div>

          {/* Upload Progress */}
          {loading && uploadStatus && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-blue-700">{uploadStatus}</span>
                <span className="text-sm font-medium text-blue-700">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? `${t('common.loading')}` : `${t('venue.register.submit')}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RestaurantSignup;